import { Temporal } from 'temporal-polyfill';
import { FormulaError, isFormulaError } from './types';
import { BLANK, EvalValue, toText } from './values';

/** Local calendar date — TODAY() means the viewing user's today. */
export function today(): Temporal.PlainDate {
  const now = new Date();
  return Temporal.PlainDate.from({ year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() });
}

/**
 * DATEVALUE: an ISO date/datetime string yields its calendar date AS WRITTEN
 * (truncation, no timezone math — so a stored "…T23:00:00Z" and its date-only
 * form compare equal); a number is epoch milliseconds, rendered as the UTC
 * calendar date; blank passes through, so a missing date stays blank instead
 * of demanding IFERROR.
 */
export function toDateText(value: EvalValue | FormulaError): EvalValue | FormulaError {
  if (isFormulaError(value) || value === BLANK) {
    return value;
  }
  if (value instanceof Temporal.PlainDate) {
    return value.toString();
  }
  if (typeof value === 'boolean') {
    return new FormulaError('#VALUE!', `${value ? 'TRUE' : 'FALSE'} is not a date`);
  }
  if (typeof value === 'string') {
    const match = /^(\d{4}-\d{2}-\d{2})([T ].*)?$/.exec(value.trim());
    return match ? match[1] : new FormulaError('#VALUE!', `"${value}" is not an ISO date`);
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return new FormulaError('#VALUE!', `${value} is not an epoch-milliseconds timestamp`);
  }
  return date.toISOString().slice(0, 10);
}

export type DateUnit = 'day' | 'month' | 'year';

/** A unit as DATEADD/DATEDIFF read it: case-insensitive, singular or plural, or its first letter. */
export function parseDateUnit(value: EvalValue): DateUnit | FormulaError {
  const unit = toText(value).trim().toLowerCase();
  switch (unit) {
    case 'd':
    case 'day':
    case 'days':
      return 'day';
    case 'm':
    case 'month':
    case 'months':
      return 'month';
    case 'y':
    case 'year':
    case 'years':
      return 'year';
    default:
      return new FormulaError(
        '#VALUE!',
        `"${toText(value)}" is not a date unit — use "day", "month" or "year"`
      );
  }
}

/**
 * A date argument as a calendar date — a date as it is; ISO text or an epoch
 * number normalized the way DATEVALUE normalizes, then read by Temporal,
 * which rejects a "2026-13-45" that only has the ISO shape. A blank stays
 * BLANK.
 */
export function toPlainDate(
  value: EvalValue | FormulaError
): Temporal.PlainDate | typeof BLANK | FormulaError {
  if (value instanceof Temporal.PlainDate) {
    return value;
  }
  const text = toDateText(value);
  if (isFormulaError(text) || text === BLANK) {
    return text;
  }
  const iso = toText(text);
  try {
    return Temporal.PlainDate.from(iso);
  } catch {
    return new FormulaError('#VALUE!', `"${iso}" is not a real calendar date`);
  }
}

/** ISO text only names years 1–9999; Temporal reaches further, with a sign and six digits. */
export function isoDateText(date: Temporal.PlainDate): string | FormulaError {
  if (date.year < 1 || date.year > 9999) {
    return new FormulaError('#NUM!', 'The date is out of range');
  }
  return date.toString();
}
